import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  StrictMode,
  lazy,
} from "react";
import { useTranslation } from "react-i18next";
import ThemeFullscreenBar from "./FullScreenBar";
import { QuestionMarkInverseMinor } from "@shopify/polaris-icons";

import { Navigate, Routes, Route, useParams } from "react-router-dom";

import TabContainer from "../../../../../components/components/TabContainer";

import { AppContext } from "../../../../../components/global/appContext";
import {
  useGetThemeTemplates,
  useUpdateThemeTemplates,
} from "../../../../../hooks/queries/themes";
import ThemeTemplatesSkeleton from "./Skeleton";

import useFullscreen from "../../../../../hooks/useFullscreen";

const TemplateEditor = lazy(() => import('./TemplateEditors/TemplateEditor.jsx'));

export default function ThemeTemplatesEdit(props) {
  const { t } = useTranslation();
  useFullscreen();

  let { id, "*": activeTemplateSlug } = useParams();
  const { setMessage, appProxyLandingURL } = useContext(AppContext);

  const { data, isError } = useGetThemeTemplates(id);
  useEffect(() => setThemeTemplates(data), [data]);
  const [themeTemplates, setThemeTemplates] = useState(data);

  const updateTemplatesMutation = useUpdateThemeTemplates(id, {
    onSuccess: (response) => {
      setMessage(t("themes.templates.actions.save") + " successfully");
      setThemeTemplates(response);
    },
    onError: (error) => {
      setMessage(t("themes.templates.errors.error") + " - templates couldn't be saved", true);
      // Merge errors from server into current templates so we keep unsaved etc..
      const returnedTemplates = error.response.data;
      let newThemeTemplates = Object.entries({ ...themeTemplates }).map(
        ([key, template]) => {
          const errors = returnedTemplates[key]?.errors;

          if (errors) {
            return [
              key,
              { ...template, errors: returnedTemplates[key].errors },
            ];
          } else {
            return [key, template];
          }
        }
      );
      setThemeTemplates(Object.fromEntries(newThemeTemplates));
    },
  });

  const activeTemplate = useMemo(() => {
    if (!themeTemplates) return null;
    return Object.values(themeTemplates).find(
      (template) => template.slug == activeTemplateSlug
    );
  }, [activeTemplateSlug, themeTemplates]);

  function handleTemplateChange(templateKey, key, value) {
    setThemeTemplates((oldThemeContent) => {
      let newThemeContent = {
        ...oldThemeContent,
        [templateKey]: {
          ...oldThemeContent[templateKey],
          [key]: value,
        },
      };
      if (key == "content") {
        newThemeContent[templateKey].unsaved =
          JSON.stringify(value) !== JSON.stringify(data[templateKey].content);
      }
      return newThemeContent;
    });
  }

  const formatTemplateTitle = (str) => {
    if (!str) throw "Cannot capilatize undefined";
    
    // Map template names to translation keys
    const templateNameMap = {
      'landing': t("themes.templates.items.landing"),
      'directory': t("themes.templates.items.directory"),
      'country': t("themes.templates.items.country"),
      'region': t("themes.templates.items.region"),
      'city': t("themes.templates.items.city"),
      'location': t("themes.templates.items.location"),
      'language': t("themes.templates.items.language"),
      'settings': t("themes.templates.items.settings"),
      'stylesheet': t("themes.templates.items.stylesheet")
    };
    
    // If we have a translation for this template name, use it
    if (templateNameMap[str.toLowerCase()]) {
      return templateNameMap[str.toLowerCase()];
    }
    
    // Fallback to the original capitalization logic
    let strings = str.split(" ");
    return strings
      .map((string) => string.charAt(0).toLocaleUpperCase() + string.slice(1))
      .join(" ");
  };

  const templatesToNavigationSections = (templates) => {
    return templates.map((template) => {
      return {
        label: formatTemplateTitle(template.name),
        url: template.slug,
        badge: Object.keys(template.errors).length
          ? t("themes.templates.badges.errors")
          : template.unsaved && t("themes.templates.badges.unsaved"),
      };
    });
  };

  const anyUnsavedChanges = useMemo(() => {
    if (!themeTemplates) return false;
    return Object.values(themeTemplates).some((t) => t.unsaved);
  }, [themeTemplates]);

  const anyErrors = useMemo(() => {
    if (!themeTemplates) return false;
    return Object.values(themeTemplates).some((t) => t?.errors?.content);
  }, [themeTemplates]);

  if (isError) return <p>{t("themes.templates.errors.error")}</p>;
  if (!themeTemplates) return <ThemeTemplatesSkeleton />;

  return (
    <div className="Theme-Template__Wrapper">
      <ThemeFullscreenBar
        returnURL="/themes/live"
        activeTemplate={activeTemplate}
        anyUnsavedChanges={anyUnsavedChanges}
        previewURL={appProxyLandingURL}
        saveAction={{
          onClick: () => updateTemplatesMutation.mutate(themeTemplates),
          loading: updateTemplatesMutation.isLoading,
          disabled: !anyUnsavedChanges,
        }}
        discardAction={{
          onClick: () => setThemeTemplates(data),
          disabled: !anyUnsavedChanges && !anyErrors,
        }}
      />
      <TabContainer
        useLastPath
        sections={[
          {
            title: t("themes.templates.categories.templates"),
            items: templatesToNavigationSections(
              Object.values(themeTemplates).filter(
                (template) => template.content_type == "liquid"
              )
            ),
          },
          {
            title: t("themes.templates.categories.schemas"),
            separator: true,
            items: templatesToNavigationSections(
              Object.values(themeTemplates).filter(
                (template) => template.content_type == "json"
              )
            ),
          },
          {
            title: t("themes.templates.categories.styles"),
            separator: true,
            items: templatesToNavigationSections(
              Object.values(themeTemplates).filter(
                (template) => template.content_type == "scss"
              )
            ),
          },
          {
            separator: true,
            items: [],
            fill: true,
          },
          {
            separator: true,
            items: [
              {
                label: t("themes.templates.categories.liquidReference"),
                onClick: console.log(),
                icon: QuestionMarkInverseMinor,
              },
            ],
          },
        ]}
      >
        <Routes>
          <Route index element={<Navigate to="location" />} />
          {Object.entries(themeTemplates).map(([templateKey, template]) => {
            return (
              <Route
                path={template.slug}
                element={
                  <TemplateEditor
                    template={themeTemplates[templateKey]}
                    handleTemplateChange={(key, value) => {
                      handleTemplateChange(templateKey, key, value);
                    }}
                    mode={template.content_type}
                    id={`${template.slug}-editor`}
                    name={`${template.slug}`}
                    key={template.id}
                  />
                }
                key={template.id}
              />
            );
          })}
        </Routes>
      </TabContainer>
    </div>
  );
}

// function saveTheme() {
//   axios
//     .put(`/themes/${id}/update_templates`, {
//       theme: themeTemplates,
//     })
//     .then((response) => {
//       setThemeErrors([]);
//       setUnsavedTemplates([]);
//       setTemplatesEditingMeta([]);
//       setThemeTemplates(response.data);
//       appContext.setMessage("Saved templates successfully");
//       // setEditingMeta(false);
//     })
//     .catch(function (error) {
//       if (error.response.status == "422") {
//         setThemeErrors(error.response.data);
//         appContext.setMessage("Please check your templates", true);
//       } else {
//         appContext.setMessage("Something went wrong", true);
//       }
//     });
// }

// const bannerMarkup =
//   themeErrors.length !== 0 ? (
//     <Layout.Section>
//       <Banner title="Errors in templates" status="critical">
//         <List>
//           {Object.keys(themeErrors).map((errorKey) => (
//             <List.Item key={errorKey}>
//               <b>
//                 {capitalize(
//                   errorKey.replaceAll(".", " ").replaceAll("_", " ")
//                 )}
//               </b>
//               : {themeErrors[errorKey].join(" ")}
//             </List.Item>
//           ))}
//         </List>
//       </Banner>
//     </Layout.Section>
//   ) : null;
